import { atom } from 'jotai';

type CookieOptions = {
  maxAge?: number;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
};

/**
 * Gets a cookie value by name
 */
const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
};

/**
 * Sets a cookie with the given name and value
 */
const setCookie = (name: string, value: string, options: CookieOptions = {}) => {
  if (typeof document === 'undefined') return;

  const {
    maxAge = 365 * 24 * 60 * 60, // Default: 1 year
    path = '/',
    domain,
    secure = false,
    sameSite = 'lax',
  } = options;

  let cookieString = `${name}=${value}; max-age=${maxAge}; path=${path}; SameSite=${sameSite}`;

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  if (secure) {
    cookieString += '; secure';
  }

  document.cookie = cookieString;
};

/**
 * Creates a Jotai atom that persists its value in a cookie
 *
 * @param key - The cookie name
 * @param initialValue - The default value if cookie doesn't exist
 * @param options - Cookie options (maxAge, path, domain, secure, sameSite)
 * @returns A Jotai atom with cookie persistence
 */
export const atomWithCookie = <T>(key: string, initialValue: T, options?: CookieOptions) => {
  const getInitialValue = (): T => {
    const cookieValue = getCookie(key);
    if (cookieValue === null) return initialValue;

    try {
      return JSON.parse(cookieValue) as T;
    } catch {
      // If parsing fails, treat as string
      return cookieValue as T;
    }
  };

  const baseAtom = atom(getInitialValue());

  const derivedAtom = atom(
    (get) => get(baseAtom),
    (get, set, update: T) => {
      set(baseAtom, update);
      const valueToStore = typeof update === 'string' ? update : JSON.stringify(update);
      setCookie(key, valueToStore, options);
    },
  );

  return derivedAtom;
};
